import i18n from '@/plugins/i18n';

type Absent<T, K extends keyof T> = { [k in Exclude<keyof T, K>]?: undefined };
type OneOf<T> =
  | { [k in keyof T]?: undefined }
  | (keyof T extends infer K
      ? K extends string & keyof T
        ? { [k in K]: T[K] } & Absent<T, K>
        : never
      : never);

// common
export interface CommonLocale {
  language: string;
  country: string;
}

// datetime
export interface IDate {
  year: number;
  month: number;
  day: number;
}
export interface IDateRange {
  start?: IDate;
  end?: IDate;
}

export interface TimePeriod {
  unit: TimePeriodUnitEnum;
  count: number;
}

export enum TimePeriodUnitEnum {
  UNKNOWN = 'UNKNOWN',
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
  SECOND = 'SECOND',
  MINUTE = 'MINUTE',
  HOUR = 'HOUR',
}

// i18n
export enum LanguageEnum {
  ENGLISH = 'ENGLISH',
  DANISH = 'DANISH',
  DUTCH = 'DUTCH',
  FINNISH = 'FINNISH',
  FRENCH = 'FRENCH',
  GERMAN = 'GERMAN',
  HEBREW = 'HEBREW',
  ITALIAN = 'ITALIAN',
  JAPANESE = 'JAPANESE',
  KOREAN = 'KOREAN',
  NORWEGIAN = 'NORWEGIAN',
  POLISH = 'POLISH',
  PORTUGUESE = 'PORTUGUESE',
  RUSSIAN = 'RUSSIAN',
  SPANISH = 'SPANISH',
  SWEDISH = 'SWEDISH',
  CHINESE = 'CHINESE',
  CZECH = 'CZECH',
  GREEK = 'GREEK',
  ICELANDIC = 'ICELANDIC',
  LATVIAN = 'LATVIAN',
  LITHUANIAN = 'LITHUANIAN',
  ROMANIAN = 'ROMANIAN',
  HUNGARIAN = 'HUNGARIAN',
  ESTONIAN = 'ESTONIAN',
  TG_UNKNOWN_LANGUAGE = 'TG_UNKNOWN_LANGUAGE',
  UNKNOWN_LANGUAGE = 'UNKNOWN_LANGUAGE',
  BULGARIAN = 'BULGARIAN',
  CROATIAN = 'CROATIAN',
  SERBIAN = 'SERBIAN',
  IRISH = 'IRISH',
  GALICIAN = 'GALICIAN',
  TAGALOG = 'TAGALOG',
  TURKISH = 'TURKISH',
  UKRAINIAN = 'UKRAINIAN',
  HINDI = 'HINDI',
  MACEDONIAN = 'MACEDONIAN',
  BENGALI = 'BENGALI',
  INDONESIAN = 'INDONESIAN',
  LATIN = 'LATIN',
  MALAY = 'MALAY',
  MALAYALAM = 'MALAYALAM',
  WELSH = 'WELSH',
  NEPALI = 'NEPALI',
  TELUGU = 'TELUGU',
  ALBANIAN = 'ALBANIAN',
  TAMIL = 'TAMIL',
  BELARUSIAN = 'BELARUSIAN',
  JAVANESE = 'JAVANESE',
  OCCITAN = 'OCCITAN',
  URDU = 'URDU',
  BIHARI = 'BIHARI',
  GUJARATI = 'GUJARATI',
  THAI = 'THAI',
  ARABIC = 'ARABIC',
  CATALAN = 'CATALAN',
  ESPERANTO = 'ESPERANTO',
  BASQUE = 'BASQUE',
  INTERLINGUA = 'INTERLINGUA',
  KANNADA = 'KANNADA',
  PUNJABI = 'PUNJABI',
  SCOTS_GAELIC = 'SCOTS_GAELIC',
  SWAHILI = 'SWAHILI',
  SLOVENIAN = 'SLOVENIAN',
  MARATHI = 'MARATHI',
  MALTESE = 'MALTESE',
  VIETNAMESE = 'VIETNAMESE',
  FRISIAN = 'FRISIAN',
  SLOVAK = 'SLOVAK',
  CHINESE_T = 'CHINESE_T',
  FAROESE = 'FAROESE',
  SUNDANESE = 'SUNDANESE',
  UZBEK = 'UZBEK',
  AMHARIC = 'AMHARIC',
  AZERBAIJANI = 'AZERBAIJANI',
  GEORGIAN = 'GEORGIAN',
  TIGRINYA = 'TIGRINYA',
  PERSIAN = 'PERSIAN',
  BOSNIAN = 'BOSNIAN',
  SINHALESE = 'SINHALESE',
  NORWEGIAN_N = 'NORWEGIAN_N',
  PORTUGUESE_P = 'PORTUGUESE_P',
  PORTUGUESE_B = 'PORTUGUESE_B',
  XHOSA = 'XHOSA',
  ZULU = 'ZULU',
  GUARANI = 'GUARANI',
  SESOTHO = 'SESOTHO',
  TURKMEN = 'TURKMEN',
  KYRGYZ = 'KYRGYZ',
  BRETON = 'BRETON',
  TWI = 'TWI',
  YIDDISH = 'YIDDISH',
  SERBO_CROATIAN = 'SERBO_CROATIAN',
  SOMALI = 'SOMALI',
  UIGHUR = 'UIGHUR',
  KURDISH = 'KURDISH',
  MONGOLIAN = 'MONGOLIAN',
  ARMENIAN = 'ARMENIAN',
  LAOTHIAN = 'LAOTHIAN',
  SINDHI = 'SINDHI',
  RHAETO_ROMANCE = 'RHAETO_ROMANCE',
  AFRIKAANS = 'AFRIKAANS',
  LUXEMBOURGISH = 'LUXEMBOURGISH',
  BURMESE = 'BURMESE',
  KHMER = 'KHMER',
  TIBETAN = 'TIBETAN',
  DHIVEHI = 'DHIVEHI',
  CHEROKEE = 'CHEROKEE',
  SYRIAC = 'SYRIAC',
  LIMBU = 'LIMBU',
  ORIYA = 'ORIYA',
  ASSAMESE = 'ASSAMESE',
  CORSICAN = 'CORSICAN',
  INTERLINGUE = 'INTERLINGUE',
  KAZAKH = 'KAZAKH',
  LINGALA = 'LINGALA',
  MOLDAVIAN = 'MOLDAVIAN',
  PASHTO = 'PASHTO',
  QUECHUA = 'QUECHUA',
  SHONA = 'SHONA',
  TAJIK = 'TAJIK',
  TATAR = 'TATAR',
  TONGA = 'TONGA',
  YORUBA = 'YORUBA',
  CREOLES_AND_PIDGINS_ENGLISH_BASED = 'CREOLES_AND_PIDGINS_ENGLISH_BASED',
  CREOLES_AND_PIDGINS_FRENCH_BASED = 'CREOLES_AND_PIDGINS_FRENCH_BASED',
  CREOLES_AND_PIDGINS_PORTUGUESE_BASED = 'CREOLES_AND_PIDGINS_PORTUGUESE_BASED',
  CREOLES_AND_PIDGINS_OTHER = 'CREOLES_AND_PIDGINS_OTHER',
  MAORI = 'MAORI',
  WOLOF = 'WOLOF',
  ABKHAZIAN = 'ABKHAZIAN',
  AFAR = 'AFAR',
  AYMARA = 'AYMARA',
  BASHKIR = 'BASHKIR',
  BISLAMA = 'BISLAMA',
  DZONGKHA = 'DZONGKHA',
  FIJIAN = 'FIJIAN',
  GREENLANDIC = 'GREENLANDIC',
  HAUSA = 'HAUSA',
  HAITIAN_CREOLE = 'HAITIAN_CREOLE',
  INUPIAK = 'INUPIAK',
  INUKTITUT = 'INUKTITUT',
  KASHMIRI = 'KASHMIRI',
  KINYARWANDA = 'KINYARWANDA',
  MALAGASY = 'MALAGASY',
  NAURU = 'NAURU',
  OROMO = 'OROMO',
  RUNDI = 'RUNDI',
  SAMOAN = 'SAMOAN',
  SANGO = 'SANGO',
  SANSKRIT = 'SANSKRIT',
  SISWANT = 'SISWANT',
  TSONGA = 'TSONGA',
  TSWANA = 'TSWANA',
  VOLAPUK = 'VOLAPUK',
  ZHUANG = 'ZHUANG',
  KHASI = 'KHASI',
  SCOTS = 'SCOTS',
  GANDA = 'GANDA',
  MANX = 'MANX',
  MONTENEGRIN = 'MONTENEGRIN',
  AKAN = 'AKAN',
  IGBO = 'IGBO',
  MAURITIAN_CREOLE = 'MAURITIAN_CREOLE',
  HAWAIIAN = 'HAWAIIAN',
  CEBUANO = 'CEBUANO',
  EWE = 'EWE',
  GA = 'GA',
  HMONG = 'HMONG',
  KRIO = 'KRIO',
  LOZI = 'LOZI',
  LUBA_LULUA = 'LUBA_LULUA',
  LUO_KENYA_AND_TANZANIA = 'LUO_KENYA_AND_TANZANIA',
  NEWARI = 'NEWARI',
  NYANJA = 'NYANJA',
  OSSETIAN = 'OSSETIAN',
  PAMPANGA = 'PAMPANGA',
  PEDI = 'PEDI',
  RAJASTHANI = 'RAJASTHANI',
  SESELWA_CREOLE_FRENCH = 'SESELWA_CREOLE_FRENCH',
  TUMBUKA = 'TUMBUKA',
  VENDA = 'VENDA',
  WARAY_PHILIPPINES = 'WARAY_PHILIPPINES',
  NUM_LANGUAGES = 'NUM_LANGUAGES',
}

export enum CountryEnum {
  ABW = 'ABW',
  AFG = 'AFG',
  AGO = 'AGO',
  AIA = 'AIA',
  ALA = 'ALA',
  ALB = 'ALB',
  AND = 'AND',
  ARE = 'ARE',
  ARG = 'ARG',
  ARM = 'ARM',
  ASM = 'ASM',
  ATA = 'ATA',
  ATF = 'ATF',
  ATG = 'ATG',
  AUS = 'AUS',
  AUT = 'AUT',
  AZE = 'AZE',
  BDI = 'BDI',
  BEL = 'BEL',
  BEN = 'BEN',
  BES = 'BES',
  BFA = 'BFA',
  BGD = 'BGD',
  BGR = 'BGR',
  BHR = 'BHR',
  BHS = 'BHS',
  BIH = 'BIH',
  BLM = 'BLM',
  BLR = 'BLR',
  BLZ = 'BLZ',
  BMU = 'BMU',
  BOL = 'BOL',
  BRA = 'BRA',
  BRB = 'BRB',
  BRN = 'BRN',
  BTN = 'BTN',
  BVT = 'BVT',
  BWA = 'BWA',
  CAF = 'CAF',
  CAN = 'CAN',
  CCK = 'CCK',
  CHE = 'CHE',
  CHL = 'CHL',
  CHN = 'CHN',
  CIV = 'CIV',
  CMR = 'CMR',
  COD = 'COD',
  COG = 'COG',
  COK = 'COK',
  COL = 'COL',
  COM = 'COM',
  CPV = 'CPV',
  CRI = 'CRI',
  CUB = 'CUB',
  CUW = 'CUW',
  CXR = 'CXR',
  CYM = 'CYM',
  CYP = 'CYP',
  CZE = 'CZE',
  DEU = 'DEU',
  DJI = 'DJI',
  DMA = 'DMA',
  DNK = 'DNK',
  DOM = 'DOM',
  DZA = 'DZA',
  ECU = 'ECU',
  EGY = 'EGY',
  ERI = 'ERI',
  ESH = 'ESH',
  ESP = 'ESP',
  EST = 'EST',
  ETH = 'ETH',
  FIN = 'FIN',
  FJI = 'FJI',
  FLK = 'FLK',
  FRA = 'FRA',
  FRO = 'FRO',
  FSM = 'FSM',
  GAB = 'GAB',
  GBR = 'GBR',
  GEO = 'GEO',
  GGY = 'GGY',
  GHA = 'GHA',
  GIB = 'GIB',
  GIN = 'GIN',
  GLP = 'GLP',
  GMB = 'GMB',
  GNB = 'GNB',
  GNQ = 'GNQ',
  GRC = 'GRC',
  GRD = 'GRD',
  GRL = 'GRL',
  GTM = 'GTM',
  GUF = 'GUF',
  GUM = 'GUM',
  GUY = 'GUY',
  HKG = 'HKG',
  HMD = 'HMD',
  HND = 'HND',
  HRV = 'HRV',
  HTI = 'HTI',
  HUN = 'HUN',
  IDN = 'IDN',
  IMN = 'IMN',
  IND = 'IND',
  IOT = 'IOT',
  IRL = 'IRL',
  IRN = 'IRN',
  IRQ = 'IRQ',
  ISL = 'ISL',
  ISR = 'ISR',
  ITA = 'ITA',
  JAM = 'JAM',
  JEY = 'JEY',
  JOR = 'JOR',
  JPN = 'JPN',
  KAZ = 'KAZ',
  KEN = 'KEN',
  KGZ = 'KGZ',
  KHM = 'KHM',
  KIR = 'KIR',
  KNA = 'KNA',
  KOR = 'KOR',
  KWT = 'KWT',
  LAO = 'LAO',
  LBN = 'LBN',
  LBR = 'LBR',
  LBY = 'LBY',
  LCA = 'LCA',
  LIE = 'LIE',
  LKA = 'LKA',
  LSO = 'LSO',
  LTU = 'LTU',
  LUX = 'LUX',
  LVA = 'LVA',
  MAC = 'MAC',
  MAF = 'MAF',
  MAR = 'MAR',
  MCO = 'MCO',
  MDA = 'MDA',
  MDG = 'MDG',
  MDV = 'MDV',
  MEX = 'MEX',
  MHL = 'MHL',
  MKD = 'MKD',
  MLI = 'MLI',
  MLT = 'MLT',
  MMR = 'MMR',
  MNE = 'MNE',
  MNG = 'MNG',
  MNP = 'MNP',
  MOZ = 'MOZ',
  MRT = 'MRT',
  MSR = 'MSR',
  MTQ = 'MTQ',
  MUS = 'MUS',
  MWI = 'MWI',
  MYS = 'MYS',
  MYT = 'MYT',
  NAM = 'NAM',
  NCL = 'NCL',
  NER = 'NER',
  NFK = 'NFK',
  NGA = 'NGA',
  NIC = 'NIC',
  NIU = 'NIU',
  NLD = 'NLD',
  NOR = 'NOR',
  NPL = 'NPL',
  NRU = 'NRU',
  NZL = 'NZL',
  OMN = 'OMN',
  PAK = 'PAK',
  PAN = 'PAN',
  PCN = 'PCN',
  PER = 'PER',
  PHL = 'PHL',
  PLW = 'PLW',
  PNG = 'PNG',
  POL = 'POL',
  PRI = 'PRI',
  PRK = 'PRK',
  PRT = 'PRT',
  PRY = 'PRY',
  PSE = 'PSE',
  PYF = 'PYF',
  QAT = 'QAT',
  REU = 'REU',
  ROU = 'ROU',
  RUS = 'RUS',
  RWA = 'RWA',
  SAU = 'SAU',
  SDN = 'SDN',
  SEN = 'SEN',
  SGP = 'SGP',
  SGS = 'SGS',
  SHN = 'SHN',
  SJM = 'SJM',
  SLB = 'SLB',
  SLE = 'SLE',
  SLV = 'SLV',
  SMR = 'SMR',
  SOM = 'SOM',
  SPM = 'SPM',
  SRB = 'SRB',
  SSD = 'SSD',
  STP = 'STP',
  SUR = 'SUR',
  SVK = 'SVK',
  SVN = 'SVN',
  SWE = 'SWE',
  SWZ = 'SWZ',
  SXM = 'SXM',
  SYC = 'SYC',
  SYR = 'SYR',
  TCA = 'TCA',
  TCD = 'TCD',
  TGO = 'TGO',
  THA = 'THA',
  TJK = 'TJK',
  TKL = 'TKL',
  TKM = 'TKM',
  TLS = 'TLS',
  TON = 'TON',
  TTO = 'TTO',
  TUN = 'TUN',
  TUR = 'TUR',
  TUV = 'TUV',
  TWN = 'TWN',
  TZA = 'TZA',
  UGA = 'UGA',
  UKR = 'UKR',
  UMI = 'UMI',
  URY = 'URY',
  USA = 'USA',
  UZB = 'UZB',
  VAT = 'VAT',
  VCT = 'VCT',
  VEN = 'VEN',
  VGB = 'VGB',
  VIR = 'VIR',
  VNM = 'VNM',
  VUT = 'VUT',
  WLF = 'WLF',
  WSM = 'WSM',
  YEM = 'YEM',
  ZAF = 'ZAF',
  ZMB = 'ZMB',
  ZWE = 'ZWE',
  UNIVERSAL = 'UNIVERSAL',
}

export interface I18nText {
  lang?: LanguageEnum;
  text?: string;
}

// mx
export enum BookingTypeEnum {
  EACH_TIME = 'EACH_TIME',
  BULK = 'BULK',
}

export enum BookingStatusEnum {
  UNKNOWN = 'UNKNOWN',
  REQUESTED = 'REQUESTED',
  STANDBY = 'STANDBY',
  DECLINED = 'DECLINED',
  CONFIRMED = 'CONFIRMED',
  WITHDRAWN_BY_TRAVELER = 'WITHDRAWN_BY_TRAVELER',
  CANCELED_BY_TRAVELER = 'CANCELED_BY_TRAVELER',
  CANCELED_BY_SUPPLIER = 'CANCELED_BY_SUPPLIER',
  CHANGED_BY_SUPPLIER = 'CHANGED_BY_SUPPLIER',
  INVALID = 'INVALID',
}

export enum IssuedStatusEnum {
  DISABLE = 'DISABLE',
  NOT_ISSUED = 'NOT_ISSUED',
  PARTIAL = 'PARTIAL',
  ISSUED = 'ISSUED',
}

// booking
export interface IBookingSummaryData {
  bookingId: string;
  activityId: string;
  planId: string;
  activityTitle: string;
  planTitle: string;
  participantFirstName: string;
  participantLastName: string;
  agentName: string;
  issuedStatus: IssuedStatusEnum;
  supplierId: string;
  supplierName: string;
}

export interface IGDSBooking {
  locale?: CommonLocale;
  bookingId?: string;
  bookingStatus?: BookingStatusEnum;
  activityId?: string;
  activityTitle?: string;
  bundleBookingParentId?: string;
  // activityTitleForTraveler?: string; // used for subsystem shown for traveler only(i.e.: mogiri)
  planId?: string;
  planTitle?: string;
  // planTitleForTraveler?: string; // used for subsystem shown for traveler only(i.e.: mogiri)
  targetDate?: IDate;
  expirationDate?: IDate;
  bookedTimestamp?: string;
  planStartTimeId?: string;
  planStartTime?: string;
  otaId?: string;
  agentName?: string;
  planUnitItems?: BookedPlanUnitItem[];
  participantLastName?: string;
  participantFirstName?: string;
  agentBookingId?: string;
  supplierId?: string;
  supplierName?: string;
  bookingType?: BookingTypeEnum;
}

export interface BookedPlanUnitItem {
  id: string;
  count: number;
}
// MUST_ISSUE_AFTER_SPECIFIC_TICKET 为 ticket专用 LIMIT_ISSUING 为 plan专用
// https://linktivityinc.slack.com/archives/CKY5MB267/p1675668288837009?thread_ts=1675407569.152629&cid=CKY5MB267
export enum IssuingRuleId {
  UNKNOWN_RULE = 'UNKNOWN_RULE',
  CANNOT_PARTIALY_ISSUE = 'CANNOT_PARTIALY_ISSUE',
  MUST_ISSUE_AFTER_SPECIFIC_TICKET = 'MUST_ISSUE_AFTER_SPECIFIC_TICKET',
  LIMITED_ISSUING = 'LIMITED_ISSUING',
}

export interface PlanSettingPrintSpec {
  type: PlanSettingPrintSpecType;
  autoPrint: boolean;
}

export enum PlanSettingPrintSpecType {
  NONE = 'NONE',
  ISSUED_RECEIPT = 'ISSUED_RECEIPT',
  REPRINT_ISSUED_RECEIPT = 'REPRINT_ISSUED_RECEIPT',
}

export interface PlanSettingUISetting {
  bookingDetailBgcolor?: string;
  bookingFormBeepOnOpen?: boolean;
}

export type IssuingRuleLimitedIssuing = {
  ticketIds?: string[];
  maxIssuableKindQuantity?: number;
};

type BaseIssuingRule = {
  id?: IssuingRuleId;
};

export type IssuingRule = BaseIssuingRule &
  OneOf<{ targetTicketId: string; limitedIssuing: IssuingRuleLimitedIssuing }>;

export enum TicketWithAmountActionEnum {
  UNKNOWN = 'UNKNOWN',
  TICKET_ISSUE = 'TICKET_ISSUE',
  TICKET_REFUND = 'TICKET_REFUND',
}

export interface ITicketWithAmount {
  ticketId: string;
  unitId: string;
  issuableTickets: ITicketDetail[];
  issuedTickets: ITicketDetail[];
  unissuableTickets: ITicketDetail[];
  issuingRules: IssuingRule[];
  title: string;
  // titleForTraveler?: string; // used for subsystem shown for traveler only(i.e.: mogiri)
  forbiddenActions?: TicketWithAmountActionEnum[];
}

export interface IExtraInfo {
  title: string;
  value: string;
}

export interface ITicketDetail {
  issuableFrom: string;
  issuableTo: string;
  amount: number;
  disobeyedRules?: IssuingRule[];
  noPermission?: boolean;
}

export interface IIssuingDetail {
  ticketId: string;
  amount: number;
}

export type IIssuingDetailWithValidation = IIssuingDetail & { valid: boolean };

export interface IIssuingLog {
  id: string;
  bookingId: string;
  operatedTickets: IOperatedTicketDetail[];
  action: IssuingRecordActionEnum;
  operatedAt: string;
  accountId: string;
  accountName: string;
  proxySupplierId?: string;
  proxySupplierName?: string;
  proxyAccountId?: string;
  proxyAccountName?: string;
  referenceId?: string;
  forbiddenActions?: IssuingLogActionEnum[];
}

export interface IOperatedTicketDetail {
  id: string;
  title: string;
  amount: number;
}

export enum IssuingLogActionEnum {
  UNKNOWN = 'UNKNOWN',
  REVOKE = 'REVOKE',
  RECHARGE = 'RECHARGE',
}

export interface IBookingDetailLock {
  isActive?: boolean;
  accountId?: string;
  accountName?: string;
  note?: string;
  updatedAt?: string;
  forbiddenActions?: BookingDetailLockActionEnum[];
}

export enum BookingDetailLockActionEnum {
  UNKNOWN = 'UNKNOWN',
  LOCK = 'LOCK',
  UNLOCK = 'UNLOCK',
}

// permission
export interface Permission {
  ticketPermission?: TicketPermission;
  bookingPermission?: BookingPermission;
  adminPermission?: AdminPermission;
}

export interface TicketPermission {
  all?: boolean;
  ticketId?: string;
  action: TicketPermissionAllowedActionEnum;
}

export enum TicketPermissionAllowedActionEnum {
  UNKNOWN = 'UNKNOWN',
  ISSUE = 'ISSUE',
  REVOKE = 'REVOKE',
  REFUND = 'REFUND',
  RECHARGE = 'RECHARGE',
  MODIFY = 'MODIFY',
}

export interface AdminPermission {
  action: AdminPermissionAllowedActionEnum;
  targetGroupId?: string;
}

export enum AdminPermissionAllowedActionEnum {
  UNKNOWN = 'UNKNOWN',
  SUPER = 'SUPER',
  GROUP_ADMIN = 'GROUP_ADMIN',
  MODIFY_TICKET_SETTING = 'MODIFY_TICKET_SETTING',
  MODIFY_PLAN_SETTING = 'MODIFY_PLAN_SETTING',
  MODIFY_SUPPLIER_SETTING = 'MODIFY_SUPPLIER_SETTING',
  VIEW_ALL_REPORTS = 'VIEW_ALL_REPORTS',
}

export interface BookingPermission {
  action: BookingPermissionAllowedActionEnum;
}

export enum BookingPermissionAllowedActionEnum {
  UNKNOWN = 'UNKNOWN',
  LOCK = 'LOCK',
  FORCE_UNLOCK = 'FORCE_UNLOCK',
}

// report
export interface IReportSummaryData {
  accountId: string;
  accountName: string;
  activityId: string;
  activityTitle: string;
  planId: string;
  planTitle: string;
  unitId: string;
  unitTitle: string;
  actionId: IssuingRecordActionEnum;
  actionTitle: string;
  amount: number;
  bookingType: BookingTypeEnum;
}

export enum IssuingRecordActionEnum {
  UNKOWN_ACTION = 'UNKOWN_ACTION',
  ISSUE = 'ISSUE',
  REVOKE = 'REVOKE',
  REFUND = 'REFUND',
  RECHARGE = 'RECHARGE',
  ADMIN_ADJUST = 'ADMIN_ADJUST',
}

// tickets
export interface IPlanSummaryData {
  activityId: string;
  activityTitle: string;
  planId: string;
  planTitle: string;
  units: PlanUnit[];
  bookingType?: BookingTypeEnum;
}

export interface PlanUnit {
  id: string;
  title: string;
}

export interface IssuableDate {
  base: IssuableDateBaseDateTypeEnum;
  period?: TimePeriod;
  relativeFixedDate?: IDate;
  yearIncreamentBaseDate?: IDate;
  absoluteFixedDate?: IDate;
}

export enum IssuableDateBaseDateTypeEnum {
  UNKNOWN_DATE = 'UNKNOWN_DATE',
  BOOKED_DATE = 'BOOKED_DATE',
  PARTICIPATION_DATE = 'PARTICIPATION_DATE',
  EARLIEST_TICKET_ISSUED_DATE = 'EARLIEST_TICKET_ISSUED_DATE',
  LATEST_TICKET_ISSUED_DATE = 'LATEST_TICKET_ISSUED_DATE',
  EARLIEST_UNIT_ISSUED_DATE = 'EARLIEST_UNIT_ISSUED_DATE',
  LATEST_UNIT_ISSUED_DATE = 'LATEST_UNIT_ISSUED_DATE',
  EARLIEST_GLOBAL_ISSUED_DATE = 'EARLIEST_GLOBAL_ISSUED_DATE',
  LATEST_GLOBAL_ISSUED_DATE = 'LATEST_GLOBAL_ISSUED_DATE',
  RELATIVE_FIXED_DATE = 'RELATIVE_FIXED_DATE',
  ABSOLUTE_FIXED_DATE = 'ABSOLUTE_FIXED_DATE',
}

export interface SettingApplyingRule {
  id: SettingApplyingRuleEnum;
  from?: string;
  to?: string;
}

export enum SettingApplyingRuleEnum {
  UNKNOWN_RULE = 'UNKNOWN_RULE',
  BASED_ON_ISSUING_DATE = 'BASED_ON_ISSUING_DATE',
  BASED_ON_PUCHASE_DATE = 'BASED_ON_PUCHASE_DATE',
  BASED_ON_PARTICIPATION_DATE = 'BASED_ON_PARTICIPATION_DATE',
}

export enum TicketStatusEnum {
  UNKNOWN = 'UNKNOWN',
  IN_USE = 'IN_USE',
  USED = 'USED',
}

export interface TicketSetting {
  id?: string;
  supplierId: string;
  planId: string;
  unitId: string;
  issuingRules: IssuingRule[];
  issuableFrom: IssuableDate[];
  issuableTo: IssuableDate[];
  title: string;
  titlesForTraveler: I18nText[];
  applyingRule: SettingApplyingRule | null;
  isInactive: boolean;
  mogiriSetting?: MogiriSettingTicket;
  version?: string;
  priority: number | null;
}

export enum MogiriSettingSupplierColorPreset {
  WHITE = 'WHITE',
  BLACK = 'BLACK',
  BLUE = 'BLUE',
  CYAN = 'CYAN',
  GREEN = 'GREEN',
  PURPLE = 'PURPLE',
  RED = 'RED',
  YELLOW = 'YELLOW',
}

export type MogiriSettingSupplierColor = {
  preset?: MogiriSettingSupplierColorPreset;
  hex?: string;
};

export type MogiriSettingSupplierIssuedTicketBackgroundColors = {
  sun?: MogiriSettingSupplierColor;
  mon?: MogiriSettingSupplierColor;
  tue?: MogiriSettingSupplierColor;
  wed?: MogiriSettingSupplierColor;
  thu?: MogiriSettingSupplierColor;
  fri?: MogiriSettingSupplierColor;
  sat?: MogiriSettingSupplierColor;
  expired?: MogiriSettingSupplierColor;
};

export type MogiriSettingSupplier = {
  issueUndoDurationSeconds?: number;
  issuedTicketBackgroundColors?: MogiriSettingSupplierIssuedTicketBackgroundColors;
  colorPrimaryHex?: string;
  logoUrl?: string;
  logoWidth?: number;
};

export type MogiriSettingPlanBannerURLConf = {
  valid?: string;
  expired?: string;
  details?: string;
};

export type MogiriSettingPlanExtendedInfo = {
  title?: string;
  description?: string;
};

export type MogiriSettingPlanLocaledExtendedInfo = {
  lang?: LanguageEnum;
  extendedInfoList?: MogiriSettingPlanExtendedInfo[];
};

export type MogiriSettingPlan = {
  bannerUrls?: MogiriSettingPlanBannerURLConf;
  localedExtendedInfo?: MogiriSettingPlanLocaledExtendedInfo[];
};
export type TimeOfDate = {
  seconds?: number;
};

export type MogiriSettingTicketDateSepc = {
  offsetDays?: number;
  timeOfDate?: TimeOfDate;
  timeZone?: string;
};

export type MogiriSettingTicketDaysSpec = {
  validDays?: number;
  timeOfDate?: TimeOfDate;
  timeZone?: string;
};

export type MogiriSettingCode = {
  code: string;
  description: string;
};

export type MogiriSettingCautionLabel = {
  ticketStatus: TicketStatusEnum;
  label: I18nText[];
};

export type MogiriSettingTicket = {
  overrideStaffLabel?: I18nText[];
  validSeconds?: number;
  fixedTime?: MogiriSettingTicketDateSepc;
  validDays?: MogiriSettingTicketDaysSpec;
  checkInCodes?: MogiriSettingCode[];
  cautionLabels?: MogiriSettingCautionLabel[];
};

export interface PlanSetting {
  id: string;
  planId: string;
  supplierId: string;
  issuableFrom: IssuableDate[];
  issuableTo: IssuableDate[];
  issuingRules: IssuingRule[];
  applyingRule: SettingApplyingRule | null;
  printSpecs: PlanSettingPrintSpec[];
  uiSetting: PlanSettingUISetting;
  mogiriSetting: MogiriSettingPlan;
  isInactive: boolean;
  version: string;
}

// users
export interface IUserTokenData {
  accountId: string;
  username: string;
  issuedTime: string;
  expiredTime: string;
  value: string;
  supplierId: string;
  groupId: string;
  groupName: string;
  displayName: string;
  version: string;
}

export interface IExternalAccount {
  id: string;
  username: string;
  locale: CommonLocale;
  fullName: string;
  decryptedPassword: string;
  groupId: string;
  delegatedAccounts: IDelegatedAccount[];
  isInactive: boolean;
  version: string;
}

export interface IDelegatedAccount {
  supplierId: string;
  accountId: string;
  supplierName: string;
  username: string;
  fullName: string;
}

export interface IGroup {
  id?: string;
  supplierId: string;
  name: string;
  permissions: Permission[];
  isInactive: boolean;
  version?: string;
}

export const adaptRequestWithLocale = (req: any) => {
  const LANGUAGE_MAP: Record<string, string> = {
    en: LanguageEnum.ENGLISH,
    ja: LanguageEnum.JAPANESE,
    zh: LanguageEnum.CHINESE,
    zt: LanguageEnum.CHINESE_T,
    ko: LanguageEnum.KOREAN,
  };
  const language = LANGUAGE_MAP[i18n.locale] || LanguageEnum.ENGLISH;
  return Object.assign(
    {
      locale: {
        language: language,
      },
    },
    req
  );
};
